import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { DozentFilterService } from './dozent-filter.service';
import { StateCleanupService } from './state-cleanup.service';

export interface DozentFaq {
  id: string;
  /** N:M assignment to dozent-bots since migration 028. At least one
   *  id is required — the API rejects empty arrays with 400. */
  dozent_bot_ids: string[];
  client_id: string;
  dozent_user_id: string;
  question: string;
  answer: string;
  category: string;
  version: string;
  course_id?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Exercise {
  id: string;
  client_id: string;
  created_by_user_id: string;
  bot_id?: string;
  title: string;
  description?: string;
  exercise_type: string;
  content: any;
  source_bucket_id?: string;
  max_score: number;
  time_limit_minutes?: number;
  is_published: boolean;
  auto_process_submissions: boolean;
  /** Freigabe für den KI-Übungsmodus (curated exercise mode, Migration 105).
   *  Nur published + freigegebene Übungen landen im Pool der Tutor/Flex-Agents. */
  chat_practice_enabled: boolean;
  /** Aus der Creator-Bibliothek veröffentlicht (Ziel „KI-Tutor“, Migration 235):
   *  bearbeitet wird im Creator, `creator_version_no` ist die veröffentlichte Version. */
  creator_item_id?: string | null;
  creator_version_no?: number | null;
  created_at: string;
  updated_at: string;
}

export interface ExerciseParseBatch {
  batch_id: string;
  jobs: { job_id: string; filename: string }[];
}

export interface ExerciseParseJobStatus {
  job_id: string;
  filename: string;
  status: 'queued' | 'processing' | 'done' | 'error';
  // A single job may create N draft exercises (CSV fan-out). `count` +
  // `exercises` are always present on success; `exercise_id`/`title` are the
  // backward-compatible single-exercise shape, set only when count === 1.
  result:
    | (Partial<Exercise> & {
        count?: number;
        exercises?: { exercise_id: string; title?: string }[];
        exercise_id?: string;
      })
    | null;
  warnings: string[];
  error: { code: string; message: string } | null;
}

export interface ExerciseParseBatchStatus {
  batch_id: string;
  jobs: ExerciseParseJobStatus[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
  pagination?: { total: number; limit: number; offset: number };
}

export interface CourseParticipant {
  id: string;
  course_id: string;
  client_id: string;
  learner_user_id?: string;
  external_user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  enrollment_source: string;
  enrollment_status: string;
  moodle_grades?: any;
  moodle_grade_total?: string;
  enrolled_at: string;
  created_at: string;
}

export interface MoodleCourse {
  id: number;
  canonical_id?: string;
  registration_id?: string;
  registration_selectable?: boolean;
  disabled_reason?: string | null;
  site_name?: string | null;
  site_url?: string | null;
  site_identifier?: string | null;
  fullname: string;
  shortname: string;
  categoryid?: number;
  summary?: string;
  startdate?: number;
  enddate?: number;
  local_course_id?: string;
  participant_count?: number;
  is_synced?: boolean;
}

export interface MoodleRefreshStatusKind {
  total: number;
  done: number;
  failed: number;
  running: number;
  queued: number;
}

export interface MoodleRefreshStatus {
  state: 'idle' | 'running' | 'running_with_errors' | 'completed' | 'completed_with_errors';
  total: number;
  done: number;
  failed: number;
  pending: number;
  running: number;
  queued: number;
  percent: number;
  started_at?: string | null;
  by_kind?: Record<string, MoodleRefreshStatusKind>;
}

export interface DozentLearnerNotificationMarker {
  learner_user_id: string;
  messages: number;
  exercises: number;
}

export interface TestExerciseRecipient {
  id: string;
  user_id?: string | null;
  user_id_source?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class DozentService {
  private tutorsBase = `${environment.tutorsBackend}`;
  private filesBase = `${environment.filesBackend}`;

  faqs$ = new BehaviorSubject<DozentFaq[]>([]);
  exercises$ = new BehaviorSubject<Exercise[]>([]);
  courses$ = new BehaviorSubject<any[]>([]);
  participants$ = new BehaviorSubject<CourseParticipant[]>([]);
  moodleCourses$ = new BehaviorSubject<MoodleCourse[]>([]);
  loading$ = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService,
    private dozentFilter: DozentFilterService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  /** Append the active dozent_user_id filter to a query string if set. */
  private withDozentFilter(params: string): string {
    const id = this.dozentFilter.selected;
    if (!id) return params;
    return `${params}${params.includes('?') ? '&' : '?'}dozent_user_id=${id}`;
  }

  // ── FAQs ──

  listFaqs(botId?: string) {
    let params = '?active_only=false';
    if (botId) params += `&bot_id=${botId}`;
    params = this.withDozentFilter(params);
    return this.http.get(`${this.tutorsBase}/dozent/faqs${params}`).pipe(
      map((d: any) => d.data ?? d),
      tap((data: DozentFaq[]) => this.faqs$.next(data))
    );
  }

  createFaq(faq: Partial<DozentFaq>) {
    return this.http.post(`${this.tutorsBase}/dozent/faqs`, faq).pipe(
      map((d: any) => d.data ?? d),
      tap(() => this.listFaqs().subscribe())
    );
  }

  updateFaq(faqId: string, faq: Partial<DozentFaq>) {
    return this.http.put(`${this.tutorsBase}/dozent/faqs/${faqId}`, faq).pipe(
      map((d: any) => d.data ?? d),
      tap(() => this.listFaqs().subscribe())
    );
  }

  deleteFaq(faqId: string) {
    return this.http.delete(`${this.tutorsBase}/dozent/faqs/${faqId}`).pipe(
      tap(() => this.listFaqs().subscribe())
    );
  }

  // ── Exercises ──

  listExercises(botId?: string) {
    let params = botId ? `?bot_id=${botId}` : '';
    params = this.withDozentFilter(params);
    this.loading$.next(true);
    return this.http.get(`${this.tutorsBase}/exercises${params}`).pipe(
      map((d: any) => d.data ?? d),
      tap((data: Exercise[]) => {
        this.exercises$.next(data);
        this.loading$.next(false);
      })
    );
  }

  getExercise(id: string) {
    return this.http.get(`${this.tutorsBase}/exercises/${id}`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  createExercise(exercise: Partial<Exercise>) {
    return this.http.post(`${this.tutorsBase}/exercises`, exercise).pipe(
      map((d: any) => d.data ?? d),
      tap(() => this.listExercises().subscribe())
    );
  }

  updateExercise(id: string, exercise: Partial<Exercise>) {
    return this.http.put(`${this.tutorsBase}/exercises/${id}`, exercise).pipe(
      map((d: any) => d.data ?? d),
      tap(() => this.listExercises().subscribe())
    );
  }

  deleteExercise(id: string) {
    return this.http.delete(`${this.tutorsBase}/exercises/${id}`).pipe(
      tap(() => this.listExercises().subscribe())
    );
  }

  // ── Exercise Import (parse uploaded documents via al-filemanager) ──

  parseExerciseDocuments(files: File[], language?: string) {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    if (language) form.append('language', language);
    return this.http.post<ExerciseParseBatch>(
      `${this.filesBase}/exercises/parse`,
      form
    );
  }

  getExerciseParseBatch(batchId: string) {
    return this.http.get<ExerciseParseBatchStatus>(
      `${this.filesBase}/exercises/parse/${batchId}`
    );
  }

  // ── Courses & Participants ──

  private pageParams(opts?: { limit?: number; offset?: number; search?: string }): HttpParams {
    let params = new HttpParams();
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.offset != null) params = params.set('offset', String(opts.offset));
    if (opts?.search) params = params.set('search', opts.search);
    return params;
  }

  private collectPages<T>(
    loadPage: (offset: number) => Observable<PaginatedResponse<T>>,
    limit = 100,
    offset = 0,
    acc: T[] = [],
  ): Observable<T[]> {
    return loadPage(offset).pipe(
      switchMap((res) => {
        const data = res.data ?? [];
        const next = [...acc, ...data];
        const hasMore = res.has_more ?? ((res.total ?? res.pagination?.total ?? next.length) > offset + data.length);
        return hasMore ? this.collectPages(loadPage, limit, offset + limit, next) : of(next);
      })
    );
  }

  listCoursesPage(opts?: { limit?: number; offset?: number; search?: string }) {
    return this.http.get<PaginatedResponse<any>>(`${this.filesBase}/courses/client-courses`, {
      params: this.pageParams(opts),
    }).pipe(
      tap((res) => this.courses$.next(res.data ?? []))
    );
  }

  listCourses() {
    return this.collectPages<any>(
      (offset) => this.listCoursesPage({ limit: 100, offset }),
    ).pipe(
      tap((data: any[]) => this.courses$.next(data))
    );
  }

  listParticipantsPage(courseId: string, opts?: { limit?: number; offset?: number; search?: string }) {
    return this.http.get<PaginatedResponse<CourseParticipant>>(
      `${this.filesBase}/courses/${courseId}/participants`,
      { params: this.pageParams(opts) },
    ).pipe(
      tap((res) => this.participants$.next(res.data ?? []))
    );
  }

  listParticipants(courseId: string) {
    return this.collectPages<CourseParticipant>(
      (offset) => this.listParticipantsPage(courseId, { limit: 100, offset }),
    ).pipe(
      tap((data: CourseParticipant[]) => this.participants$.next(data))
    );
  }

  refreshMoodleCourseAndParticipantSync() {
    return this.http
      .post(`${this.tutorsBase}/moodle/canonical/refresh`, {})
      .pipe(map((d: any) => d.data ?? d));
  }

  refreshMoodleParticipantsForCourse(courseId: string) {
    return this.http
      .post(`${this.tutorsBase}/moodle/canonical/local-courses/${courseId}/participants/refresh`, {})
      .pipe(map((d: any) => d.data ?? d));
  }

  refreshMoodleCourse(courseId: string) {
    return this.http
      .post(`${this.tutorsBase}/moodle/canonical/local-courses/${courseId}/refresh`, {})
      .pipe(map((d: any) => d.data ?? d));
  }

  getMoodleRefreshStatus(since?: string | null) {
    let params = new HttpParams();
    if (since) params = params.set('since', since);
    return this.http.get<MoodleRefreshStatus>(
      `${this.tutorsBase}/moodle/canonical/refresh-status`,
      { params },
    );
  }

  addParticipant(courseId: string, participant: Partial<CourseParticipant>) {
    return this.http.post(`${this.filesBase}/courses/${courseId}/participants`, participant).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  updateParticipant(courseId: string, participantId: string, data: Partial<CourseParticipant>) {
    return this.http.put(`${this.filesBase}/courses/${courseId}/participants/${participantId}`, data).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  removeParticipant(courseId: string, participantId: string) {
    return this.http.delete(`${this.filesBase}/courses/${courseId}/participants/${participantId}`);
  }

  importParticipantsCsv(courseId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.filesBase}/courses/${courseId}/participants/import-csv`, formData).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  createCourse(course: { course_name: string; description?: string; source_type?: string }) {
    return this.http.post(`${this.filesBase}/course`, course).pipe(
      map((d: any) => d.data?.course ?? d.data ?? d)
    );
  }

  deleteCourse(courseId: string) {
    return this.http.delete(`${this.filesBase}/course/${courseId}`);
  }

  importMoodleCourses(moodleCourseIds: number[]) {
    return this.http.post(`${this.filesBase}/courses/participants/sync-moodle-bulk`, {
      moodle_course_ids: moodleCourseIds,
    }).pipe(map((d: any) => d.data ?? d));
  }

  /** Lightweight Moodle import for the dozent participants flow:
   *  creates local course rows + syncs participants only — no content
   *  sections/modules/embeddings (that's the Wissensbasis flow). */
  importMoodleCoursesMeta(
    courses: Array<{ moodle_course_id: number; course_name?: string; registration_id?: string }>,
    includeGrades = true
  ) {
    return this.http
      .post(`${this.filesBase}/courses/moodle/import-meta`, {
        courses,
        include_grades: includeGrades,
      })
      .pipe(map((d: any) => d.data ?? d));
  }

  // ── Moodle ──

  /** Fetch available Moodle courses from the canonical layer
   *  (al-tutor `/moodle/canonical/courses`).
   *
   *  Replaces the legacy live-call to al-filemanager. Server-side
   *  permission filter scopes the list automatically: admin sees all
   *  client courses, dozent sees only those linked to bots they have
   *  `user_bot_access` on. The canonical wire shape is mapped to the
   *  legacy `MoodleCourse` interface so the picker dialog keeps working:
   *  `id` ← `moodle_course_id` (Moodle int), date fields ← unix timestamps. */
  listMoodleCoursesPage(opts?: { limit?: number; offset?: number; search?: string }) {
    return this.http
      .get<PaginatedResponse<any>>(`${this.tutorsBase}/moodle/canonical/courses`, {
        params: this.pageParams(opts),
      })
      .pipe(
        map((res) => ({
          ...res,
          data: (res.data || []).map((c) => ({
            id: c.moodle_course_id,
            canonical_id: c.id,
            registration_id: c.registration_id,
            registration_selectable: c.registration_selectable,
            disabled_reason: c.disabled_reason,
            site_name: c.site_name,
            site_url: c.site_url,
            site_identifier: c.site_identifier,
            fullname: c.fullname ?? '',
            shortname: c.shortname ?? '',
            summary: c.summary ?? '',
            startdate: c.start_date ? Math.floor(new Date(c.start_date).getTime() / 1000) : undefined,
            enddate: c.end_date ? Math.floor(new Date(c.end_date).getTime() / 1000) : undefined,
            // Computed in the canonical endpoint via JOIN on moodle_enrollments —
            // gives the dozent picker dialog actual participant counts instead
            // of the constant `0` it used to render before this field was wired
            // through. Refreshes whenever the worker syncs enrollments (~24h).
            participant_count: c.participant_count ?? 0,
          })) as MoodleCourse[],
        })),
        tap((res) => this.moodleCourses$.next(res.data ?? []))
      );
  }

  listMoodleCourses() {
    return this.collectPages<MoodleCourse>(
      (offset) => this.listMoodleCoursesPage({ limit: 100, offset }),
    );
  }

  syncMoodleParticipants(courseId: string) {
    return this.http.post(`${this.filesBase}/courses/${courseId}/participants/sync-moodle`, {}).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  importMoodleParticipants(courseId: string, moodleCourseId: number, includeGrades = true, registrationId?: string) {
    const body: any = {
      moodle_course_id: moodleCourseId,
      include_grades: includeGrades,
    };
    if (registrationId) {
      body.registration_id = registrationId;
    }
    return this.http
      .post(`${this.filesBase}/courses/${courseId}/participants/import-from-moodle`, body)
      .pipe(map((d: any) => d.data ?? d));
  }

  syncMoodleParticipantsBulk(courseIds: string[], includeGrades = true) {
    return this.http.post(`${this.filesBase}/courses/participants/sync-moodle-bulk`, {
      course_ids: courseIds,
      include_grades: includeGrades,
    }).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  getParticipantGrades(courseId: string) {
    return this.http.get(`${this.filesBase}/courses/${courseId}/participants/grades`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  // ── Participant Profile ──

  getParticipantLearnerProfile(participantId: string) {
    return this.http.get(`${this.tutorsBase}/dozent/participants/${participantId}/learner-profile`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  /** Lernstandsbericht: state per learning objective (learning-state PRD L8.2). */
  getParticipantLearningState(participantId: string) {
    return this.http.get(`${this.tutorsBase}/dozent/participants/${participantId}/learning-state`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  /** Kompetenzprofil: levels per area, Bloom level and DQR competence (PRD competency-profile). */
  getParticipantCompetencyProfile(participantId: string) {
    return this.http.get(`${this.tutorsBase}/dozent/participants/${participantId}/competency-profile`).pipe(
      map((d: any) => d.data ?? null)
    );
  }

  getParticipantExercises(participantId: string, courseId?: string | null) {
    const url = `${this.tutorsBase}/dozent/participants/${participantId}/exercises`;
    const params: any = {};
    if (courseId) params.course_id = courseId;
    return this.http.get(url, { params }).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  getParticipantConversations(participantId: string, courseId?: string | null) {
    const url = `${this.tutorsBase}/dozent/participants/${participantId}/conversations`;
    const params: any = {};
    if (courseId) params.course_id = courseId;
    return this.http.get(url, { params }).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  // ── Course exercises (assign / dispatch) ──

  listOwnExercises() {
    const url = `${this.tutorsBase}/dozent/exercises/own${this.withDozentFilter('')}`;
    return this.http.get(url).pipe(map((d: any) => d.data ?? d));
  }

  listOwnDozentBots() {
    return this.http.get(`${this.tutorsBase}/dozent/bots/own`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  listCourseAssignments(courseId: string) {
    return this.http
      .get(`${this.tutorsBase}/dozent/courses/${courseId}/assignments`)
      .pipe(map((d: any) => d.data ?? d));
  }

  dispatchExercise(
    courseId: string,
    payload: {
      exercise_id: string;
      bot_id: string;
      participant_user_ids: string[];
      instructions?: string;
      due_date?: string;
      scheduled_publish_at?: string;
    }
  ) {
    return this.http
      .post(`${this.tutorsBase}/dozent/courses/${courseId}/assignments/dispatch`, payload)
      .pipe(map((d: any) => d.data ?? d));
  }

  getTestExerciseRecipient() {
    return this.http
      .get(`${this.tutorsBase}/dozent/exercises/test-recipient`)
      .pipe(map((d: any) => d.data ?? d));
  }

  dispatchTestExercise(payload: {
    exercise_id: string;
    bot_id: string;
    instructions?: string;
    due_date?: string;
    scheduled_publish_at?: string;
  }) {
    return this.http
      .post(`${this.tutorsBase}/dozent/exercises/test-dispatch`, payload)
      .pipe(map((d: any) => d.data ?? d));
  }

  deleteAssignment(assignmentId: string) {
    return this.http.delete(`${this.tutorsBase}/dozent/assignments/${assignmentId}`);
  }

  listAssignmentSubmissions(assignmentId: string) {
    return this.http
      .get(`${this.tutorsBase}/dozent/assignments/${assignmentId}/submissions`)
      .pipe(map((d: any) => d.data ?? d));
  }

  saveSubmissionFeedback(
    submissionId: string,
    payload: { dozent_score?: number | null; dozent_feedback?: string | null }
  ) {
    return this.http
      .put(`${this.tutorsBase}/dozent/submissions/${submissionId}/feedback`, payload)
      .pipe(map((d: any) => d.data ?? d));
  }

  getOnlineStatuses(ids: string[]) {
    return this.http.get(`${this.tutorsBase}/dozent/participants/online-status?ids=${ids.join(',')}`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  // ── Conversations ──

  listConversations(learnerId?: string, status?: string) {
    let params = '';
    if (learnerId) params += `?learner_user_id=${learnerId}`;
    if (status) params += `${params ? '&' : '?'}status=${status}`;
    params = this.withDozentFilter(params);
    return this.http.get(`${this.tutorsBase}/dozent/conversations${params}`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  createConversation(data: { learner_user_id: string; bot_id?: string; course_id?: string | null; title?: string; initial_message?: string }) {
    return this.http.post(`${this.tutorsBase}/dozent/conversations`, data).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  getConversationMessages(conversationId: string) {
    return this.http.get(`${this.tutorsBase}/dozent/conversations/${conversationId}/messages`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  sendConversationMessage(conversationId: string, message: string) {
    return this.http.post(`${this.tutorsBase}/dozent/conversations/${conversationId}/messages`, { message }).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  resolveConversation(conversationId: string) {
    return this.http.put(`${this.tutorsBase}/dozent/conversations/${conversationId}/resolve`, {}).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  // ── Notifications ──

  listNotifications(unreadOnly = false) {
    return this.http.get(`${this.tutorsBase}/dozent/notifications?unread_only=${unreadOnly}`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  getNotificationUnreadCount() {
    return this.http.get(`${this.tutorsBase}/dozent/notifications/unread-count`).pipe(
      map((d: any) => d.data?.count ?? 0)
    );
  }

  listNotificationLearnerMarkers() {
    const params = this.withDozentFilter('');
    return this.http.get(`${this.tutorsBase}/dozent/notifications/learner-markers${params}`).pipe(
      map((d: any) => (d.data ?? d) as DozentLearnerNotificationMarker[])
    );
  }

  markNotificationRead(notificationId: string) {
    return this.http.put(`${this.tutorsBase}/dozent/notifications/${notificationId}/read`, {});
  }

  markAllNotificationsRead() {
    return this.http.put(`${this.tutorsBase}/dozent/notifications/read-all`, {});
  }

  // ── Learning Suggestions ──

  listLearningSuggestions(status = 'pending') {
    return this.http.get(`${this.tutorsBase}/dozent/learning-suggestions?status=${status}`).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  approveLearningSuggestion(suggestionId: string, botId: string) {
    return this.http.put(`${this.tutorsBase}/dozent/learning-suggestions/${suggestionId}/approve?bot_id=${botId}`, {}).pipe(
      map((d: any) => d.data ?? d)
    );
  }

  rejectLearningSuggestion(suggestionId: string) {
    return this.http.put(`${this.tutorsBase}/dozent/learning-suggestions/${suggestionId}/reject`, {});
  }

  // ── Knowledge Files ──

  /**
   * Get or create the personal knowledge bucket for the current dozent.
   * Backend resolves the bucket per (client_id, owner_user_id) so each
   * dozent has an isolated FAQ/knowledge base.
   */
  getOrCreateKnowledgeBucket() {
    return this.http
      .get(`${this.filesBase}/dozent/knowledge-bucket`)
      .pipe(map((d: any) => d.data ?? d));
  }

  listKnowledgeFiles(bucketId: string) {
    return this.http.get(`${this.filesBase}/bucket/${bucketId}/entries`).pipe(
      map((d: any) => {
        const data = d.data ?? d;
        return data.datatypes?.FILE || [];
      })
    );
  }

  deleteKnowledgeFile(fileId: number) {
    return this.http.delete(`${this.filesBase}/file/${fileId}`);
  }

  private resetState() {
    this.faqs$.next([]);
    this.exercises$.next([]);
    this.courses$.next([]);
    this.participants$.next([]);
    this.moodleCourses$.next([]);
    this.loading$.next(false);
  }
}
